import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TableCell, Typography } from '@mui/material';
import HttpServices from 'src/services/httpService';
import { postsModuleURL } from 'src/services/urlService';
import { CommonHelper } from 'src/utils/commonHelper';

const SelectionContext = createContext(null);
export function BulkPostDelete({ rows = [], page, rowsPerPage, isOrgEvent, onDeleted, children }) {
  const { t } = useTranslation();
  const brand = CommonHelper.GetCurrentBrandId();
  const [selected, setSelected] = useState([]);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const generation = useRef(0);
  const submitting = useRef(false);
  useEffect(() => {
    generation.current += 1;
    setSelected([]); setConfirm(false); setError(''); setBusy(false); submitting.current = false;
    return () => { generation.current += 1; };
  }, [isOrgEvent, brand]);
  // Pagination and sorting must not discard selections from other pages.
  // Remove only posts that disappeared or are no longer manageable.
  useEffect(() => {
    const allowed = new Set(rows.filter((row) => row.has_edit_access === true).map((row) => row.id));
    setSelected((current) => current.filter((id) => allowed.has(id)));
  }, [rows]);
  const visible = rows.slice(page * rowsPerPage, (page + 1) * rowsPerPage);
  const eligible = visible.filter((row) => row.has_edit_access === true).map((row) => row.id);
  const toggle = (id) => {
    if (busy || !eligible.includes(id)) return;
    setSelected((current) => current.includes(id) ? current.filter((value) => value !== id) : [...current, id].slice(0, 100));
  };
  const remove = () => {
    if (submitting.current || !selected.length) return;
    submitting.current = true; setBusy(true); setError('');
    const version = generation.current;
    HttpServices.Post({
      url: postsModuleURL.deleteSchedules,
      body: { schedule_post_ids: selected, is_org_event: !!isOrgEvent },
      successCallback: () => {
        if (version !== generation.current) return;
        submitting.current = false; setBusy(false); setConfirm(false); setSelected([]);
        onDeleted();
      },
      failureCallback: (message) => {
        if (version !== generation.current) return;
        submitting.current = false; setBusy(false);
        setError(message || t('bulk_posts.failure', 'Unable to delete the selected posts. Refresh and try again.'));
      },
    });
  };
  return <SelectionContext.Provider value={{ selected, toggle, eligible, busy, setSelected }}>
    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}
      sx={{ px: 2, py: 0.5, minHeight: 48, bgcolor: selected.length ? 'action.selected' : 'background.paper', borderRadius: '8px 8px 0 0', borderBottom: '1px solid', borderColor: 'divider' }}>
      <Typography variant="body2" sx={{ minWidth: 0, lineHeight: 1.5, fontWeight: selected.length ? 600 : 400, color: selected.length ? 'text.primary' : 'text.secondary' }}>{selected.length ? t('bulk_posts.selected', { count: selected.length, defaultValue: '{{count}} selected across pages (maximum 100)' }) : t('bulk_posts.hint', 'Select up to 100 posts across pages to delete. Only posts you can manage are selectable.')}</Typography>
      <Button size="small" color="error" variant={selected.length ? 'contained' : 'outlined'} sx={{ flexShrink: 0, whiteSpace: 'nowrap', minHeight: 32, px: 2 }} disabled={!selected.length || busy} onClick={() => { setError(''); setConfirm(true); }}>{t('bulk_posts.delete', 'Delete selected')}</Button>
    </Stack>
    <Box sx={{ pointerEvents: busy ? 'none' : 'auto' }}>{children}</Box>
    <Dialog open={confirm} onClose={() => { if (!busy) setConfirm(false); }}>
      <DialogTitle>{t('bulk_posts.title', 'Delete selected posts?')}</DialogTitle>
      <DialogContent>
        <Typography>{t('bulk_posts.confirm', { count: selected.length, defaultValue: 'Delete {{count}} selected scheduled-post records? This cannot be undone. Posts already published on social platforms will not be deleted.' })}</Typography>
        {isOrgEvent && <Alert severity="warning" sx={{ mt: 2 }}>{t('bulk_posts.org_warning', 'Organization-level selection also deletes the linked scheduled records for all included brands.')}</Alert>}
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button disabled={busy} onClick={() => setConfirm(false)}>{t('common.cancel', 'Cancel')}</Button>
        <Button color="error" variant="contained" disabled={busy} onClick={remove}>{busy ? t('bulk_posts.deleting', 'Deleting...') : t('bulk_posts.delete', 'Delete selected')}</Button>
      </DialogActions>
    </Dialog>
  </SelectionContext.Provider>;
}

export function BulkPostHeader() {
  const context = useContext(SelectionContext);
  const { t } = useTranslation();
  if (!context) return null;
  const { eligible, selected, busy, setSelected } = context;
  const selectedOnPage = eligible.filter((id) => selected.includes(id)).length;
  return <Checkbox size="small" sx={{ p: 1, color: 'common.white', '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: 'common.white' } }} disabled={busy || !eligible.length || (selected.length >= 100 && !selectedOnPage)} checked={eligible.length > 0 && selectedOnPage === eligible.length}
    indeterminate={selectedOnPage > 0 && selectedOnPage < eligible.length}
    inputProps={{ 'aria-label': t('bulk_posts.select_page', 'Select all deletable posts on this page') }}
    onChange={(event) => {
      const checked = event.target.checked;
      setSelected((current) => checked ? [...new Set([...current, ...eligible])].slice(0, 100) : current.filter((id) => !eligible.includes(id)));
    }} />;
}

export function BulkPostCell({ row }) {
  const context = useContext(SelectionContext);
  const { t } = useTranslation();
  if (!context) return null;
  return <TableCell padding="none" align="center" sx={{ width: 48, minWidth: 48, maxWidth: 48, border: 0, verticalAlign: 'middle' }} onClick={(event) => event.stopPropagation()}>
    <Checkbox size="small" sx={{ p: 1 }} disabled={context.busy || row.has_edit_access !== true || (context.selected.length >= 100 && !context.selected.includes(row.id))}
      checked={context.selected.includes(row.id)} onChange={() => context.toggle(row.id)}
      inputProps={{ 'aria-label': t('bulk_posts.select_post', 'Select post') }} />
  </TableCell>;
}
